import React, { useCallback, useEffect, useRef, useState } from 'react';

import researchService from '../../services/researchService';
import TagChip from './TagChip';

const CANNED_FORMULAS = [
  {
    id: 'sec_or_dynamic',
    label: 'SEC First Found  (A OR B OR C ...)',
    params: [],
    buildDynamic: values => {
      const tags = (values || [])
        .map(v => String(v || '').replace(/^SEC:/, '').trim())
        .filter(Boolean);
      return tags.length ? `{SECSET:${tags.join('|')}}` : '';
    },
  },
  {
    id: 'sum2',
    label: 'Sum  (A + B)',
    params: [{ k: 'a', lbl: 'Row A' }, { k: 'b', lbl: 'Row B' }],
    build: p => `{${p.a}} + {${p.b}}`,
  },
  {
    id: 'sum3',
    label: 'Sum  (A + B + C)',
    params: [{ k: 'a', lbl: 'Row A' }, { k: 'b', lbl: 'Row B' }, { k: 'c', lbl: 'Row C' }],
    build: p => `{${p.a}} + {${p.b}} + {${p.c}}`,
  },
  {
    id: 'diff',
    label: 'Difference  (A - B)',
    params: [{ k: 'a', lbl: 'A  (from)' }, { k: 'b', lbl: 'B  (subtract)' }],
    build: p => `{${p.a}} - {${p.b}}`,
  },
  {
    id: 'ratio',
    label: 'Ratio / Margin  (A / B)',
    params: [{ k: 'a', lbl: 'Numerator A' }, { k: 'b', lbl: 'Denominator B' }],
    build: p => `{${p.a}} / {${p.b}}`,
  },
  {
    id: 'gross_margin',
    label: 'Gross Margin  ((A - B) / A)',
    params: [{ k: 'a', lbl: 'Revenue A' }, { k: 'b', lbl: 'Cost B' }],
    build: p => `({${p.a}} - {${p.b}}) / {${p.a}}`,
  },
  {
    id: 'pct_of',
    label: '% of Total  (A / Total)',
    params: [{ k: 'a', lbl: 'Value A' }, { k: 'total', lbl: 'Total (denominator)' }],
    build: p => `{${p.a}} / {${p.total}}`,
  },
  {
    id: 'ebitda',
    label: 'EBITDA  (Op. Income + D&A)',
    params: [{ k: 'opinc', lbl: 'Operating Income' }, { k: 'da', lbl: 'D&A' }],
    build: p => `{${p.opinc}} + {${p.da}}`,
  },
];

const SUPPORTED_EXPR_RE = /^[0-9eE.+\-*/()\s]+$/;
const PLACEHOLDER_RE = /\{([^}]+)\}/g;
const IFERROR_RE = /^IFERROR\((.*?),\s*(?:""|'')\s*\)$/i;

function isSupportedNormalizationFormula(template) {
  const source = String(template || '').trim();
  if (!source) return true;

  let expr = source.startsWith('=') ? source.slice(1).trim() : source;
  const iferrorMatch = IFERROR_RE.exec(expr);
  if (iferrorMatch) {
    expr = iferrorMatch[1].trim();
  }

  const placeholders = [];
  expr = expr.replace(PLACEHOLDER_RE, (_, raw) => {
    placeholders.push(String(raw || '').trim());
    return '1';
  });

  for (const token of placeholders) {
    if (!token) return false;
    if (token.startsWith('SEC:')) {
      if (!token.slice(4).trim()) return false;
      continue;
    }
    if (token.startsWith('SECSET:') || token.startsWith('SECSUM:')) {
      const rest = token.split(':', 2)[1] || '';
      const parts = rest.split('|').map(p => p.trim()).filter(Boolean);
      if (parts.length === 0) return false;
      continue;
    }
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(token)) {
      return false;
    }
  }

  return SUPPORTED_EXPR_RE.test(expr);
}

function CannedFormulaBuilder({ allRows, onApply, onCancel }) {
  const labelMap = React.useMemo(() => {
    const m = {};
    for (const row of (allRows || [])) {
      if (row.canonical_key) m[row.canonical_key] = row.label || row.canonical_key;
    }
    return m;
  }, [allRows]);

  const keys = Object.keys(labelMap);
  const secKeys = keys.filter(k => k.startsWith('SEC:'));
  const [formulaIdx, setFormulaIdx] = useState(0);
  const formula = CANNED_FORMULAS[formulaIdx];
  const [params, setParams] = useState(() =>
    Object.fromEntries(formula.params.map((p, i) => [p.k, keys[i] || keys[0] || '']))
  );
  const [dynamicSecRefs, setDynamicSecRefs] = useState(() => {
    if (secKeys.length >= 2) return [secKeys[0], secKeys[1]];
    if (secKeys.length === 1) return [secKeys[0]];
    return [];
  });

  useEffect(() => {
    setParams(Object.fromEntries(
      CANNED_FORMULAS[formulaIdx].params.map((p, i) => [p.k, keys[i] || keys[0] || ''])
    ));
    if (CANNED_FORMULAS[formulaIdx].id === 'sec_or_dynamic') {
      if (secKeys.length >= 2) setDynamicSecRefs([secKeys[0], secKeys[1]]);
      else if (secKeys.length === 1) setDynamicSecRefs([secKeys[0]]);
      else setDynamicSecRefs([]);
    }
  }, [formulaIdx, allRows]);

  const setParam = (k, v) => setParams(prev => ({ ...prev, [k]: v }));
  const canApply = formula.id === 'sec_or_dynamic'
    ? dynamicSecRefs.filter(Boolean).length > 0
    : (keys.length > 0 && formula.params.every(p => params[p.k]));

  const setDynamicRef = (idx, value) => {
    setDynamicSecRefs(prev => prev.map((v, i) => (i === idx ? value : v)));
  };

  const addDynamicRef = () => {
    if (secKeys.length === 0) return;
    setDynamicSecRefs(prev => [...prev, secKeys[0]]);
  };

  const removeDynamicRef = (idx) => {
    setDynamicSecRefs(prev => prev.filter((_, i) => i !== idx));
  };

  return (
    <div style={{ border: '1px solid #84b6f4', borderRadius: 6, padding: '6px 8px', background: '#f0f6ff', marginTop: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 10, color: '#084298', fontWeight: 600 }}>Formula type:</span>
        <select
          value={formulaIdx}
          onChange={e => setFormulaIdx(Number(e.target.value))}
          style={{ fontSize: 11, flex: 1, padding: '2px 4px', borderRadius: 4, border: '1px solid #84b6f4' }}
        >
          {CANNED_FORMULAS.map((f, i) => <option key={f.id} value={i}>{f.label}</option>)}
        </select>
      </div>

      {formula.id === 'sec_or_dynamic' ? (
        <>
          {dynamicSecRefs.map((ref, idx) => (
            <div key={`sec-dyn-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
              <span style={{ fontSize: 10, color: '#6c757d', width: 110, flexShrink: 0, textAlign: 'right' }}>{`SEC Tag ${idx + 1}:`}</span>
              <select
                value={ref || ''}
                onChange={e => setDynamicRef(idx, e.target.value)}
                style={{ fontSize: 11, flex: 1, padding: '1px 4px', borderRadius: 4, border: '1px solid #ced4da', background: '#cfe2ff', color: '#084298' }}
              >
                {secKeys.map(k => <option key={k} value={k}>{labelMap[k]}</option>)}
                {ref && !secKeys.includes(ref) && (
                  <option value={ref}>{ref}</option>
                )}
              </select>
              <button
                onClick={() => removeDynamicRef(idx)}
                disabled={dynamicSecRefs.length <= 1}
                style={{ fontSize: 10, padding: '1px 6px', border: '1px solid #ced4da', borderRadius: 3, background: 'transparent', color: '#6c757d', cursor: dynamicSecRefs.length > 1 ? 'pointer' : 'default' }}
                title="Remove SEC tag"
              >x</button>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
            <button
              onClick={addDynamicRef}
              disabled={secKeys.length === 0}
              style={{ fontSize: 10, padding: '2px 8px', border: '1px dashed #adb5bd', borderRadius: 3, background: 'transparent', color: '#6c757d', cursor: secKeys.length > 0 ? 'pointer' : 'default' }}
            >+ add SEC tag</button>
          </div>
        </>
      ) : formula.params.map(p => (
        <div key={p.k} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
          <span style={{ fontSize: 10, color: '#6c757d', width: 110, flexShrink: 0, textAlign: 'right' }}>{p.lbl}:</span>
          <select
            value={params[p.k] || ''}
            onChange={e => setParam(p.k, e.target.value)}
            style={{ fontSize: 11, flex: 1, padding: '1px 4px', borderRadius: 4, border: '1px solid #ced4da', background: '#cfe2ff', color: '#084298' }}
          >
            {keys.map(k => <option key={k} value={k}>{labelMap[k]}</option>)}
            {params[p.k] && !keys.includes(params[p.k]) && (
              <option value={params[p.k]}>{params[p.k]}</option>
            )}
          </select>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 4, marginTop: 6, justifyContent: 'flex-end' }}>
        <button
          onClick={onCancel}
          style={{ fontSize: 10, padding: '2px 8px', border: '1px solid #ced4da', borderRadius: 3, background: 'transparent', color: '#6c757d', cursor: 'pointer' }}
        >Cancel</button>
        <button
          disabled={!canApply}
          onClick={() => {
            const built = formula.id === 'sec_or_dynamic'
              ? formula.buildDynamic(dynamicSecRefs)
              : formula.build(params);
            onApply(built);
          }}
          style={{ fontSize: 10, padding: '2px 8px', border: 'none', borderRadius: 3, background: canApply ? '#0d6efd' : '#adb5bd', color: 'white', cursor: canApply ? 'pointer' : 'default' }}
        >Apply</button>
      </div>
    </div>
  );
}

function FormulaTemplateEditor({ template, allRows, onSave }) {
  const labelMap = React.useMemo(() => {
    const m = {};
    for (const row of (allRows || [])) {
      if (row.canonical_key) m[row.canonical_key] = row.label || row.canonical_key;
    }
    return m;
  }, [allRows]);

  const canonicalKeys = Object.keys(labelMap);

  const parseTokens = (tpl) => {
    if (!tpl) return [];
    const tokens = [];
    const re = /\{([^}]+)\}/g;
    let last = 0;
    let match;
    while ((match = re.exec(tpl)) !== null) {
      if (match.index > last) tokens.push({ type: 'lit', val: tpl.slice(last, match.index) });
      tokens.push({ type: 'key', val: match[1] });
      last = match.index + match[0].length;
    }
    if (last < tpl.length) tokens.push({ type: 'lit', val: tpl.slice(last) });
    return tokens;
  };

  const buildTemplate = toks => toks.map(t => (t.type === 'key' ? `{${t.val}}` : t.val)).join('');

  const [tokens, setTokens] = useState(() => parseTokens(template || ''));
  const lastSaved = useRef(template || '');
  const [rawMode, setRawMode] = useState(false);
  const [rawText, setRawText] = useState(template || '');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if ((template || '') !== lastSaved.current) {
      lastSaved.current = template || '';
      setTokens(parseTokens(template || ''));
      setRawText(template || '');
    }
  }, [template]);

  const commit = (newTokens) => {
    const tpl = buildTemplate(newTokens);
    if (!isSupportedNormalizationFormula(tpl)) {
      setValidationError('Unsupported formula syntax for normalization. Use arithmetic + placeholders only.');
      return;
    }
    setValidationError('');
    lastSaved.current = tpl;
    setTokens(newTokens);
    setRawText(tpl);
    onSave(tpl);
  };

  const commitRaw = (tpl) => {
    if (!isSupportedNormalizationFormula(tpl)) {
      setValidationError('Unsupported formula syntax for normalization.');
      return false;
    }
    setValidationError('');
    lastSaved.current = tpl;
    setTokens(parseTokens(tpl));
    setRawText(tpl);
    onSave(tpl);
    return true;
  };

  const toggleRaw = () => {
    if (rawMode) {
      const ok = commitRaw(rawText);
      if (!ok) return;
    }
    setRawMode(r => !r);
  };

  const changeKey = (i, newKey) => {
    const t = [...tokens];
    t[i] = { type: 'key', val: newKey };
    commit(t);
  };

  const changeLit = (i, newVal) => {
    const t = [...tokens];
    t[i] = { type: 'lit', val: newVal };
    commit(t);
  };

  const removeToken = i => commit(tokens.filter((_, j) => j !== i));

  const addKey = () => {
    const defaultKey = canonicalKeys[0] || 'revenue';
    const sep = tokens.length > 0 ? { type: 'lit', val: ' + ' } : null;
    commit(sep ? [...tokens, sep, { type: 'key', val: defaultKey }] : [{ type: 'key', val: defaultKey }]);
  };

  if (canonicalKeys.length === 0) return null;

  const editBtn = (
    <button
      onClick={toggleRaw}
      title={rawMode ? 'Switch to visual editor' : 'Edit raw formula text'}
      style={{ background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer', color: rawMode ? '#0d6efd' : '#adb5bd', fontSize: 12, lineHeight: 1, flexShrink: 0 }}
    >
      <i className={rawMode ? 'bi bi-check2' : 'bi bi-pencil'} />
    </button>
  );

  if (rawMode) {
    return (
      <div style={{ display: 'grid', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <input
            type="text"
            autoFocus
            value={rawText}
            onChange={e => setRawText(e.target.value)}
            onBlur={() => {
              const ok = commitRaw(rawText);
              if (ok) setRawMode(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const ok = commitRaw(rawText);
                if (ok) setRawMode(false);
              }
              if (e.key === 'Escape') {
                setValidationError('');
                setRawMode(false);
              }
            }}
            style={{
              flexGrow: 1,
              fontSize: 11,
              fontFamily: 'monospace',
              padding: '2px 4px',
              border: '1px solid #84b6f4',
              borderRadius: 4,
              background: '#f8f9fa',
              color: '#212529',
              outline: 'none',
            }}
            placeholder={'e.g. IFERROR({revenue}/{cost_of_revenue}, "")'}
          />
          {editBtn}
        </div>
        {validationError && (
          <div style={{ color: '#b02a37', fontSize: 10 }}>
            {validationError}
          </div>
        )}
      </div>
    );
  }

  const pickerBtn = (
    <button
      onClick={() => setPickerOpen(o => !o)}
      title="Pick a formula template"
      style={{ background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer', color: pickerOpen ? '#0d6efd' : '#adb5bd', fontSize: 12, lineHeight: 1, flexShrink: 0 }}
    >
      <i className="bi bi-calculator" />
    </button>
  );

  if (tokens.length === 0 && !pickerOpen) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <button
          onClick={() => setRawMode(true)}
          style={{
            background: 'none',
            border: '1px dashed #dee2e6',
            borderRadius: 4,
            padding: '2px 8px',
            cursor: 'pointer',
            color: '#adb5bd',
            fontSize: 11,
            fontFamily: 'monospace',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
          title="Set Excel formula template"
        >
          <i className="bi bi-code-slash" style={{ fontSize: 10 }} />
          Set Excel formula...
        </button>
        {pickerBtn}
      </div>
    );
  }

  if (pickerOpen) {
    return (
      <CannedFormulaBuilder
        allRows={allRows}
        onApply={(tpl) => { commitRaw(tpl); setPickerOpen(false); }}
        onCancel={() => setPickerOpen(false)}
      />
    );
  }

  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, fontFamily: 'monospace', fontSize: 11, minHeight: 24 }}>
      {tokens.map((tok, i) => (
        tok.type === 'key' ? (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <select
              value={tok.val}
              onChange={e => changeKey(i, e.target.value)}
              style={{
                fontSize: 11,
                fontFamily: 'monospace',
                padding: '1px 4px',
                borderRadius: 4,
                background: '#cfe2ff',
                border: '1px solid #84b6f4',
                color: '#084298',
                cursor: 'pointer',
              }}
            >
              {canonicalKeys.map(k => <option key={k} value={k}>{labelMap[k]}</option>)}
              {!canonicalKeys.includes(tok.val) && <option value={tok.val}>{tok.val}</option>}
            </select>
            <button
              onClick={() => removeToken(i)}
              style={{ background: 'none', border: 'none', padding: '0 1px', cursor: 'pointer', color: '#adb5bd', fontSize: 13, lineHeight: 1 }}
              title="Remove"
            >x</button>
          </span>
        ) : (
          <input
            key={i}
            type="text"
            value={tok.val}
            onChange={e => changeLit(i, e.target.value)}
            style={{
              fontSize: 11,
              fontFamily: 'monospace',
              padding: '1px 0',
              width: `${Math.max(16, tok.val.length * 7)}px`,
              border: 'none',
              borderBottom: '1px dashed #dee2e6',
              background: 'transparent',
              color: '#6c757d',
              outline: 'none',
            }}
          />
        )
      ))}
      <button
        onClick={addKey}
        style={{
          fontSize: 10,
          padding: '1px 6px',
          border: '1px dashed #adb5bd',
          borderRadius: 3,
          background: 'transparent',
          color: '#6c757d',
          cursor: 'pointer',
        }}
        title="Add metric reference"
      >+ key</button>
      {pickerBtn}
      {editBtn}
      </div>
      {validationError && (
        <div style={{ color: '#b02a37', fontSize: 10 }}>
          {validationError}
        </div>
      )}
    </div>
  );
}

function normalizeRule(rule) {
  if (Array.isArray(rule)) {
    return { sec_tags: rule, sec_tag_groups: [], excel_formula_template: '' };
  }
  if (rule && typeof rule === 'object') {
    return {
      ...rule,
      sec_tags: Array.isArray(rule.sec_tags) ? rule.sec_tags : [],
      sec_tag_groups: Array.isArray(rule.sec_tag_groups) ? rule.sec_tag_groups : [],
      excel_formula_template: typeof rule.excel_formula_template === 'string'
        ? rule.excel_formula_template
        : '',
    };
  }
  return { sec_tags: [], sec_tag_groups: [], excel_formula_template: '' };
}

function normalizeConfig(config) {
  const normalized = {};
  Object.entries(config || {}).forEach(([key, value]) => {
    normalized[key] = normalizeRule(value);
  });
  return normalized;
}

/**
 * Searchable dropdown for picking a known XBRL tag name.
 * Identical to the one in ModelConfigTab — extracted here for local use.
 */
function TagPicker({ knownTags, excludeTags, onPick, onCancel }) {
  const [query, setQuery] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const matches = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const candidates = knownTags.filter(t => !excludeTags.includes(t));
    if (!q) return candidates.slice(0, 80);
    return candidates.filter(t => t.toLowerCase().includes(q)).slice(0, 80);
  }, [query, knownTags, excludeTags]);

  useEffect(() => { setHighlightIdx(0); }, [matches]);

  const pick = (tag) => { if (tag) onPick(tag); };

  const scrollIntoView = (idx) => {
    const item = listRef.current?.children[idx];
    if (item) item.scrollIntoView({ block: 'nearest' });
  };

  const handleKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => { const n = Math.min(i + 1, matches.length - 1); scrollIntoView(n); return n; });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => { const n = Math.max(i - 1, 0); scrollIntoView(n); return n; });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (matches[highlightIdx]) pick(matches[highlightIdx]);
      else if (query.trim()) pick(query.trim());
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
        <input
          ref={inputRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKey}
          placeholder={knownTags.length ? 'Search XBRL tag…' : 'XBRL tag name…'}
          style={{ fontSize: 11, padding: '2px 6px', border: '1px solid #0d6efd', borderRadius: 4, width: 220, fontFamily: 'monospace' }}
        />
        <button className="btn btn-secondary btn-sm py-0 px-2" style={{ fontSize: 11 }} onClick={onCancel}>✕</button>
      </div>
      {matches.length > 0 && (
        <ul
          ref={listRef}
          style={{
            position: 'absolute', top: '100%', left: 0, zIndex: 999,
            background: '#fff', border: '1px solid #ced4da', borderRadius: 4,
            margin: 0, padding: 0, listStyle: 'none', maxHeight: 220, overflowY: 'auto', minWidth: 260,
          }}
        >
          {matches.map((t, i) => (
            <li
              key={t}
              onMouseDown={() => pick(t)}
              style={{
                padding: '4px 10px', fontSize: 11, fontFamily: 'monospace', cursor: 'pointer',
                background: i === highlightIdx ? '#0d6efd' : '#fff',
                color: i === highlightIdx ? '#fff' : '#000',
              }}
            >
              {t}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Human-readable labels for normalized column names
const COLUMN_LABELS = {
  revenue: 'Revenue',
  cost_of_revenue: 'Cost of Revenue',
  gross_profit: 'Gross Profit',
  operating_expenses: 'Operating Expenses',
  operating_income: 'Operating Income',
  net_income: 'Net Income',
  ebitda: 'EBITDA',
  adjusted_ebitda: 'Adjusted EBITDA',
  total_assets: 'Total Assets',
  total_liabilities: 'Total Liabilities',
  total_equity: 'Total Equity',
  shareholders_equity: "Shareholders' Equity",
  cash_and_equivalents: 'Cash & Equivalents',
  total_debt: 'Total Debt',
  current_assets: 'Current Assets',
  current_liabilities: 'Current Liabilities',
  eps_basic: 'EPS (Basic)',
  eps_diluted: 'EPS (Diluted)',
  free_cash_flow: 'Free Cash Flow',
  shares_outstanding: 'Shares Outstanding',
  gross_margin: 'Gross Margin (computed)',
  net_margin: 'Net Margin (computed)',
  debt_to_equity: 'Debt / Equity (computed)',
  current_ratio: 'Current Ratio (computed)',
  roe: 'ROE (computed)',
};

const COMPUTED_COLS = new Set(['gross_margin', 'net_margin', 'debt_to_equity', 'current_ratio', 'roe']);

/**
 * SecTagNormalizationConfig — edit which SEC XBRL tags populate each
 * normalized Redshift column (and by extension each DynamoDB canonical key).
 *
 * Mirrors the SEC tag editing experience in ModelConfigTab.
 */
export default function SecTagNormalizationConfig() {
  const [normConfig, setNormConfig] = useState(null);   // { col: { sec_tags, excel_formula_template } }
  const [knownTags, setKnownTags] = useState([]);
  const [addingTagFor, setAddingTagFor] = useState(null); // col key where picker is open
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load config + known tags in parallel on mount
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      researchService.getSecTagNormalization(),
      researchService.getKnownTags(),
    ])
      .then(([cfg, tags]) => {
        if (cancelled) return;
        setNormConfig(normalizeConfig(cfg));
        setKnownTags(Array.isArray(tags) ? tags : []);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message || 'Load failed');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const removeTag = useCallback((col, tag) => {
    setNormConfig(prev => ({
      ...prev,
      [col]: {
        ...normalizeRule(prev[col]),
        sec_tags: normalizeRule(prev[col]).sec_tags.filter(t => t !== tag),
      },
    }));
  }, []);

  const addTag = useCallback((col, tag) => {
    if (!tag) return;
    setNormConfig(prev => {
      const current = normalizeRule(prev[col]);
      const existing = current.sec_tags;
      if (existing.includes(tag)) return prev;
      return {
        ...prev,
        [col]: { ...current, sec_tags: [...existing, tag] },
      };
    });
    setAddingTagFor(null);
  }, []);

  const moveTag = useCallback((col, idx, dir) => {
    setNormConfig(prev => {
      const current = normalizeRule(prev[col]);
      const tags = [...current.sec_tags];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= tags.length) return prev;
      [tags[idx], tags[newIdx]] = [tags[newIdx], tags[idx]];
      return { ...prev, [col]: { ...current, sec_tags: tags } };
    });
  }, []);

  const updateFormula = useCallback((col, formula) => {
    setNormConfig(prev => ({
      ...prev,
      [col]: {
        ...normalizeRule(prev[col]),
        excel_formula_template: formula,
      },
    }));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      await researchService.saveSecTagNormalization(normConfig);
      setSaveMsg({ type: 'success', text: 'Saved successfully.' });
    } catch (err) {
      setSaveMsg({ type: 'error', text: err?.response?.data?.detail || err.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-3 text-muted">Loading…</div>;
  }
  if (error) {
    return <div className="p-3 alert alert-danger">{error}</div>;
  }

  const cols = Object.keys(COLUMN_LABELS);
  const formulaRows = [
    ...cols.map(col => ({
      canonical_key: col,
      label: COLUMN_LABELS[col] || col,
    })),
    ...knownTags.map(tag => ({
      canonical_key: `SEC:${tag}`,
      label: `SEC Tag: ${tag}`,
    })),
  ];

  return (
    <div>
      {/* Toolbar */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <button
          className="btn btn-primary btn-sm"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {saveMsg && (
          <span
            className={`small ${saveMsg.type === 'success' ? 'text-success' : 'text-danger'}`}
          >
            {saveMsg.text}
          </span>
        )}
        <span className="text-muted small ms-auto">
          Tags are tried in order first. If none resolve, the fallback formula is used.
        </span>
      </div>

      {/* Column table */}
      <table className="table table-sm table-bordered align-middle" style={{ fontSize: 13 }}>
        <thead className="table-dark">
          <tr>
            <th style={{ width: 200 }}>Normalized Column</th>
            <th>Mapped SEC XBRL Tags (priority order)</th>
            <th style={{ width: 80 }}>Add Tag</th>
          </tr>
        </thead>
        <tbody>
          {cols.map(col => {
            const rule = normalizeRule(normConfig?.[col]);
            const tags = rule.sec_tags;
            const isComputed = COMPUTED_COLS.has(col);
            return (
              <tr key={col}>
                <td>
                  <div style={{ fontWeight: 500 }}>{COLUMN_LABELS[col] || col}</div>
                  <div className="text-muted" style={{ fontSize: 10, fontFamily: 'monospace' }}>{col}</div>
                  {isComputed && (
                    <span className="badge bg-secondary" style={{ fontSize: 9 }}>computed</span>
                  )}
                </td>
                <td>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {isComputed && (
                      <span className="text-muted small">Computed columns typically rely on a fallback formula, but SEC tags can still be configured.</span>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
                      {tags.map((tag, idx) => (
                        <span key={tag} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                          <button
                            className="btn btn-link p-0"
                            style={{ fontSize: 10, lineHeight: 1, color: '#6c757d' }}
                            title="Move up (higher priority)"
                            disabled={idx === 0}
                            onClick={() => moveTag(col, idx, -1)}
                          >↑</button>
                          <button
                            className="btn btn-link p-0"
                            style={{ fontSize: 10, lineHeight: 1, color: '#6c757d' }}
                            title="Move down (lower priority)"
                            disabled={idx === tags.length - 1}
                            onClick={() => moveTag(col, idx, 1)}
                          >↓</button>
                          <TagChip tag={tag} onRemove={() => removeTag(col, tag)} />
                        </span>
                      ))}
                      {tags.length === 0 && (
                        <span className="text-muted small">No tags mapped.</span>
                      )}
                      {addingTagFor === col && (
                        <TagPicker
                          knownTags={knownTags}
                          excludeTags={tags}
                          onPick={tag => addTag(col, tag)}
                          onCancel={() => setAddingTagFor(null)}
                        />
                      )}
                    </div>
                    <div>
                      <div className="text-muted mb-1" style={{ fontSize: 11 }}>
                        Fallback formula
                      </div>
                      <FormulaTemplateEditor
                        template={rule.excel_formula_template || ''}
                        allRows={formulaRows}
                        onSave={v => updateFormula(col, v)}
                      />
                      <div className="text-muted mt-1" style={{ fontSize: 10 }}>
                        Use {'{revenue}'} for normalized fields, {'{SEC:Revenues}'} for raw tags,
                        {' {SECSET:Revenues|SalesRevenueNet} '}for first-available (OR) tag sets,
                        and {'{SECSUM:TagA|TagB}'} for summed tag sets. Unsupported here:
                        {' {PREV:...}, {YEARSUM:...}, {YEARAVG:...}, cross-sheet refs.'}
                      </div>
                    </div>
                  </div>
                </td>
                <td>
                  {addingTagFor !== col && (
                    <button
                      className="btn btn-outline-primary btn-sm py-0 px-2"
                      style={{ fontSize: 11 }}
                      onClick={() => setAddingTagFor(col)}
                    >
                      + Tag
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
